<template>
    <div class="batchActions flex-level">
        <div class="flex-level pl-2">
            <span class="valign-middle fs-3">{{
                $l10n.n__(
                    '%d ligne sélectionnée',
                    '%d lignes sélectionnées',
                    totalSelected,
                    totalSelected,
                )
            }}</span>

            <CompassSelect
                v-model="batchAction.name"
                :disabled="!lengthSelection"
                :placeholder="`— ${$l10n.__('Choisissez une action à effectuer')} —`"
                class="batch-actions-select"
                menuClass="batch-actions-select__menu"
                @change="handleChange"
            >
                <CompassOptionGroup
                    v-for="(value, key) in batchActionsByUse"
                    :key="key"
                    :label="labelOptionGroup(key)"
                >
                    <CompassTooltip
                        v-for="action in value"
                        :key="action.name"
                        :content="$t.__('listing.ListingBatch.action_error')"
                        :disabled="isActiveBatchAction(action)"
                    >
                        <CompassDropdownSelectOption
                            :label="action.label"
                            :value="action.action"
                            :disabled="!isActiveBatchAction(action)"
                            :data-upsell="action['data-upsell']"
                            :class="dropdownOptionClass(action.name)"
                        >
                            {{ action.label }}
                            <CompassFeatureBadge
                                v-if="!isSubscriptionFeatureEnabled(action.name)"
                                variant="upsell"
                                size="small"
                            />
                        </CompassDropdownSelectOption>
                    </CompassTooltip>
                </CompassOptionGroup>
            </CompassSelect>

            <el-checkbox
                v-if="enableAllPages && visibleAllPages"
                id="selectAllPages"
                v-model="checkAllPages"
                name="selectAllPages"
                class="flex-level ml-3"
            >
                {{
                    $l10n.__('Sélectionner toutes les pages (%(count)s éléments)', {
                        count: pagination.total,
                    })
                }}
            </el-checkbox>

            <CompassButton
                :label="$t.__('common.actions.validate')"
                type="primary"
                :disabled="!lengthSelection || !batchAction.name"
                class="right valign-middle ml-3"
                @click="launchAction"
            />
        </div>
    </div>
</template>

<script>
import CursorTooltip from 'vcomponents/utilities/CursorTooltip.vue';
import { BatchAction } from './BatchAction.js';
import { MapGroupBy } from 'utils/object.js';
import Feature from 'Services/Feature';
import { createNamespacedHelpers } from 'vuex';

const subscriptionFeaturesStore = createNamespacedHelpers('subscriptionFeatures');
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    components: {
        CursorTooltip,
    },
    props: {
        listinghash: {
            type: String,
            required: true,
        },
        selection: {
            type: Array,
        },
        enableAllPages: {
            type: Boolean,
        },
    },
    data() {
        return {
            batchAction: new BatchAction(
                '',
                this.$store.state[this.listinghash].linkedtype,
                this.$store.state[this.listinghash].listingname,
            ),
            selectAllLimit: 1000,
        };
    },
    computed: {
        ...subscriptionFeaturesStore.mapGetters({
            subscriptionFeatures: 'getSubscriptionFeatures',
        }),
        pagination() {
            return this.$store.state[this.listinghash].pagination;
        },
        batchActions() {
            return this.$store.state[this.listinghash].batchActions;
        },
        listingname() {
            return this.$store.state[this.listinghash].listingname;
        },
        linkedtype() {
            return this.$store.state[this.listinghash].linkedtype;
        },
        lengthSelection() {
            return this.selection.length;
        },
        mustSelectFullRow() {
            return this.$store.state[this.listinghash].mustSelectFullRow;
        },
        visibleAllPages() {
            return this.$store.state[this.listinghash].display.visibleAllPages;
        },
        checkAllPages: {
            get() {
                return this.$store.state[this.listinghash].display.checkAllPages;
            },
            set(value) {
                this.$store.state[this.listinghash].display.checkAllPages = value;
            },
        },

        totalSelected() {
            return this.checkAllPages ? this.pagination.total : this.lengthSelection;
        },
        /**
         * Return an array of relationType selected
         * Using new Set for remove dupplication
         */
        allRelationTypeSelected() {
            return [
                ...new Set(
                    this.selection
                        .filter(({ relationType }) => relationType) // to ensure that the relationType prop exist
                        .map(({ relationType }) => relationType), // to get only the relationType prop
                ),
            ];
        },
        /**
         * Return the list of batchActions grouped by useOn
         */
        batchActionsByUse() {
            const allBatchActionsByUse = Object.fromEntries(
                MapGroupBy(this.batchActions, (action) => action.useOn?.sort().join('::')),
            );

            // Remove gocardless action if email-engine
            if (Feature.isEnabled('email-engine')) {
                for (const key in allBatchActionsByUse) {
                    allBatchActionsByUse[key] = allBatchActionsByUse[key].filter(
                        (action) => action.name !== 'gocardless',
                    );
                }
            }

            return allBatchActionsByUse;
        },
        isSubscriptionFeatureEnabled() {
            return (option) => {
                let optionName = option;

                if (optionName === 'addPromotions') {
                    optionName = 'promotion_management';
                }

                if (optionName === 'exportSepa' || optionName === 'purExportSepa') {
                    optionName = 'sepa_export';
                }

                if (
                    optionName === 'share' ||
                    optionName === 'assignMassStaff' ||
                    optionName === 'updateOwner'
                ) {
                    optionName = 'collaborative_sharing_suite';
                }

                return this.subscriptionFeatures[optionName] ?? true;
            };
        },
        dropdownOptionClass() {
            return (option) => (!this.isSubscriptionFeatureEnabled(option) ? 'upsell-action' : '');
        },
    },
    methods: {
        ...popupPanelMapActions(['open']),
        async launchAction() {
            if (this.checkAllPages && this.visibleAllPages) {
                const selection = [];
                const rows = await this.$store.dispatch(`${this.listinghash}/getAllPages`);

                rows.forEach((itm) => {
                    if (this.mustSelectFullRow) {
                        selection.push(itm);
                    } else {
                        selection.push(this.linkedtype + '_' + itm.id);
                    }
                });
                this.batchAction
                    .setSelectedRows(selection)
                    .launch(this.$store.getters[`${this.listinghash}/batchData`]);
            } else {
                const selected = [];

                this.selection.forEach((itm) => {
                    if (this.mustSelectFullRow) {
                        selected.push(itm);
                    } else {
                        selected.push(this.linkedtype + '_' + itm.id);
                    }
                });

                this.batchAction
                    .setSelectedRows(selected)
                    .launch(this.$store.getters[`${this.listinghash}/batchData`]);
            }
        },
        closeBatchAction(action) {
            if (action == 'save') {
                // Reset batchAction & selection
                this.batchAction.name = false;
                this.$refs.selectedRows.resetSelection();
                this.selectedRows = [];

                // Refetch listing
                this.fetch();
            } else if (action == 'cancel') {
                Vue.set(this.batchAction, 'open', false);
            }
        },
        /**
         * Determine if batch action can be used
         *
         * @param {Object} action - Object action for determinate if is active
         * @return {Boolean} return true  :
         *                   - if action is enabled
         *                   - or all row selected have a relationType included in "useOn" prop of action.
         *                   (if allRelationTypeSelected is empty test return true, important for other listing (opportunity, product,... ))
         */
        isActiveBatchAction(action) {
            return (
                action.enabled &&
                this.allRelationTypeSelected.every(
                    (relationType) => action.useOn && action.useOn.includes(relationType),
                )
            );
        },
        /**
         * Return the good label of group according key passed in param
         *
         * @param {String} key - key for determinate the label
         * @return {String} return label of group
         */
        labelOptionGroup(key) {
            switch (key) {
                case 'client::prospect::supplier':
                    return this.$l10n.__('Client, Prospect et Fournisseur');
                case 'client::prospect':
                    return this.$l10n.__('Client et Prospect');
                case 'client':
                    return this.$l10n.__('Client uniquement');
                case 'prospect':
                    return this.$l10n.__('Prospect uniquement');
                default:
                    return this.allRelationTypeSelected.length ? this.$l10n.__('Autre') : '';
            }
        },
        handleChange($event) {
            if (!this.isSubscriptionFeatureEnabled($event)) {
                this.open({
                    key: 'featureModal',
                    props: {},
                });
                this.$nextTick().then(() => {
                    this.batchAction.name = '';
                });
            }
        },
    },
};
</script>

<style lang="scss">
@import '~@sellsy/styleguide/src/_variables';
@import '~@sellsy/styleguide/src/_mixins';

.batchActions {
    z-index: 90;
    height: 48px;
    position: absolute;
    top: 1px;
    left: 42px;
    right: 2px;
    background-color: white;

    .el-checkbox__label {
        padding-left: spacer(2);
    }
}
.batch-actions-select {
    margin-left: var(--spacing-2);
    width: 244px;

    &__menu {
        .compass-tooltip,
        .compass-tooltip__element {
            display: block;
        }

        .upsell-action {
            color: var(--colors-common-text-standard-disabled);
        }

        .compass-feature-badge {
            flex-shrink: 0;
        }
    }
}
</style>
